import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { Col, Row, Table, Button, Modal, Form, Input, Spin, notification, Select, Tooltip } from "antd";
import { PlusOutlined, ApiFilled, SaveFilled, AppstoreFilled } from '@ant-design/icons';

import { getAssets, getAssetsByCode, getAssetCategories, createAsset, updateAsset, deleteAsset } from "../../API/assets";
import { getControlsArea } from "../../API/controls";
import { getLicences } from "../../API/licences";
import AssetSearch from "./components/AssetSearch/AssetSearch";
import Columns from './components/AssetTable/columns';

import WithPermission from "../Common/WithPermission/WithPermission";
import ExportButton from '../Common/ExportButton/ExportButton';
import PageTitle from '../Common/PageTitle/PageTitle';
import Assetia from '../Common/Assetia/Assetia';

import { useAuth } from "../Context/AuthContext";
const { Option } = Select;

const Assets = () => {
  const navigate = useNavigate();
  // Permisos
  const { hasPermission, userData, logout } = useAuth();
  // Consulta permisos: Superusuario  
  const isSuperuser = userData?.is_superuser === true;
  // Consulta permisos: Tiene rol asignado  
  const hasAnyRole = userData?.roles?.length > 0;
  // Consulta especifica de permisos
  const canAssetsRead = userData ? hasPermission("assets", "read") : false;
  const canAssetsCreate = userData ? hasPermission("assets", "create") : false;

  // Estados componente
  const [api, contextHolder] = notification.useNotification();
  const [assets, setAssets] = useState([]);
  const [controlsArea, setControlsArea] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [form] = Form.useForm();
  // Loader
  const [loading, setLoading] = useState(true);
  // Buscador
  const [filteredData, setFilteredData] = useState([]); 
  const [searchText, setSearchText] = useState('');
  // Autofocus
  const inputRef = useRef(null);
  // Max Assets
  const [maxAssetsAllowed, setMaxAssetsAllowed] = useState(null);
  // Obtener los filtros del Dashboard de la URL
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const assetsFromUrl = searchParams.get('assets')?.split(',').filter(Boolean) || [];
  const areasFromUrl = searchParams.get('areas')?.split(',').filter(Boolean) || [];
  //
  const [activeFilters, setActiveFilters] = useState({});
  // Detectar si los filtros vienen del dashboard
  const hasUrlFilters = assetsFromUrl.length > 0 || areasFromUrl.length > 0;  

  // Sincronizar filtros del dashboard con activeFilters
  useEffect(() => {
    const initialFilters = {};
    
    if (assetsFromUrl.length > 0) {
      initialFilters.asset = assetsFromUrl;
    }
    
    if (areasFromUrl.length > 0) {
      initialFilters.area = areasFromUrl;
    }
    
    // Solo setear si hay filtros del dashboard
    if (Object.keys(initialFilters).length > 0) {
      setActiveFilters(initialFilters);
    }
  }, [assetsFromUrl.join(','), areasFromUrl.join(',')]);

  useEffect(() => {
    if (isModalOpen && !editingAsset) {
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [isModalOpen, editingAsset]);

  // Cargar activos + licencia
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [assetsResponse, licencesResponse] = await Promise.all([
          getAssets(),
          getLicences()
        ]);

        setAssets(assetsResponse);
        setFilteredData(assetsResponse);

        // Buscar la licencia activa
        const activeLicence = licencesResponse?.find(l => l.active);
        if (activeLicence?.product?.number_of_assets) {
          setMaxAssetsAllowed(activeLicence.product.number_of_assets);
        }
      } catch (error) {
        api.error({ message: "Error al cargar datos de activos o licencia" });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const fetchAssets = async () => {
    setLoading(true);
    try {
      const response = await getAssets();
      setAssets(response);
      setFilteredData(response);
    } catch {
      api.error({ message: "Error al cargar los activos" });
    } finally {
      setLoading(false);
    }
  };

  // Guardar activo (crear o actualizar)
  const handleSave = async (values) => {
    try {
      if (editingAsset) {
        await updateAsset(editingAsset.code, values);
        api.success({ message: "Activo actualizado."});
      } else {
        await createAsset(values);
        api.success({ message: "Activo agregado."});
      }
      setIsModalOpen(false);
      form.resetFields();
      setEditingAsset(null);
      fetchAssets();
    } catch (error) {
      api.error({ 
        message: "Error al guardar el activo.",
        description: error?.response?.data?.name?.[0].includes("already exists") 
          ? "Ya existe un activo con ese nombre."
          : "Ocurrió un error inesperado al guardar el activo.",
        key: 'save-assets-error'
      });
    }
  };

  // Eliminar un activo
  const handleDelete = async (code) => {
    try {
      await deleteAsset(code);
      api.success({ message: "Activo eliminado."});
      fetchAssets();
    } catch {
      api.error({ message: "Error al eliminar el activo."});
    }
  };

  // Mostrar modal para agregar/editar
  const showModal = async (asset = null) => {
    if (asset) {
      try {
        setLoading(true);
        // llamado al endpoint por code/id
        const fullAsset = await getAssetsByCode(asset.code);
        setEditingAsset(fullAsset);
        form.setFieldsValue({
          ...fullAsset,
          area_id: fullAsset.area?.id ?? fullAsset.area_id,
          category_id: fullAsset.category?.id ?? fullAsset.category_id,
        });
      } catch (error) {
        api.error({ message: "Error al cargar datos del activo." });
      } finally {
        setLoading(false);
      }
    } else {
      setEditingAsset(null);
      form.resetFields();
    }

    try {
      const [areas, assetCategories] = await Promise.all([
        getControlsArea("assets"),
        getAssetCategories(),
      ]);
      const permittedAreas = areas.filter(area =>
        isSuperuser || hasPermission("assets", "create", area.id)
      );
      
      setControlsArea(permittedAreas);
      setCategories(assetCategories);
    } catch (error) {
      api.error({ message: "Error al cargar las áreas o categorías de assets." });
    }

    setIsModalOpen(true);
  };

  useEffect(() => {
    fetchAssets();
  }, []);

  const assetTableFilter = () => {
    // Función helper para aplicar filtros
    const applyFilter = (data, filterKey, filterValues) => {
      if (!filterValues || filterValues.length === 0) return data;
      
      const filterMap = {
        asset: (item) => item.name,
        area: (item) => item.area?.name,
        category: (item) => item.category?.name,
        user: (item) => item.user?.username,
      };
      
      const getter = filterMap[filterKey];
      if (!getter) return data;
      
      return data.filter(item => {
        const value = getter(item);
        return filterValues.includes(value);
      });
    };

    // Obtener datos filtrados excluyendo una columna
    const getFilteredDataForColumn = (excludeColumn) => {
      let data = [...assets];
      
      Object.entries(activeFilters).forEach(([key, values]) => {
        if (key !== excludeColumn && values && values.length > 0) {
          data = applyFilter(data, key, values);
        }
      });
      
      return data;
    };

    // Generar opciones dinámicas basadas en filtros activos
    const assetsData = getFilteredDataForColumn('asset');
    const uniqueAssets = Array.from(
      new Set(assetsData.map(a => a.name).filter(Boolean))
    ).map(name => ({ name }));

    const usersData = getFilteredDataForColumn('user');
    const uniqueUsers = Array.from(
      new Set(usersData.map(a => a.user?.username).filter(Boolean))
    ).map(username => ({ username })); 

    const areasData = getFilteredDataForColumn('area');
    const uniqueAreas = Array.from(
      new Set(areasData.map(a => a.area?.name).filter(Boolean))
    ).map(name => ({ name }));

    const categoriesData = getFilteredDataForColumn('category');
    const uniqueCategories = Array.from(
      new Set(categoriesData.map(a => a.category?.name).filter(Boolean))
    ).map(name => ({ name }));

    return { 
      assets: uniqueAssets,
      users: uniqueUsers, 
      areas: uniqueAreas,
      categories: uniqueCategories,
      dashboardAssetsFilter: assetsFromUrl ?? [],
      dashboardAreasFilter: areasFromUrl ?? [],
    };
  };

  // Manejar cambios en los filtros de la tabla
  const handleTableChange = (pagination, filters) => {
    // Guardar filtros activos
    setActiveFilters(filters);
    
    // Solo modificar URL si hay filtros del dashboard
    if (hasUrlFilters) {
      const newParams = new URLSearchParams();
      
      if (filters.asset?.length > 0) {
        newParams.set('assets', filters.asset.join(','));
      }
      if (filters.area?.length > 0) {
        newParams.set('areas', filters.area.join(','));
      }
      
      const params = new URLSearchParams(location.search);
      // Preservar otros parámetros si existen
      params.forEach((value, key) => {
        if (!['assets', 'areas'].includes(key)) {
          newParams.set(key, value);
        }
      });
      
      navigate({
        pathname: '/assets',
        search: newParams.toString() ? `?${newParams.toString()}` : ''
      }, { replace: true });
    }
    // Si NO hay filtros del dashboard, NO tocar la URL
  };

  const dashboardGeneral = JSON.parse(
    localStorage.getItem("dashboardGeneral") || "{}"
  );

  const hasAssets = dashboardGeneral?.assets?.count > 0;

  const canAccessAssets =
    (hasAnyRole && canAssetsRead) ||
    hasAssets;

  return (
    <WithPermission
      isSuperuser={isSuperuser}
      hasAnyRole={hasAnyRole}
      requiredPermission="read"
      currentPermission={canAccessAssets}
      redirectTo="/home"
    >
      <>
        {contextHolder}
        <div>
          <div className="button-container">
            <Row justify="start">
              <Col>
                <PageTitle title="Inventario de Activos Críticos" icon="assets" />
              </Col>
            </Row>

            <Row justify="start" gutter={[0, 16]}>
              <Col span={18}>
                <Tooltip
                  placement="top"
                  title={
                    (maxAssetsAllowed && assets.length >= maxAssetsAllowed)
                      ? `Alcanzó el límite máximo de ${maxAssetsAllowed} activos de su Licencia`
                      : (!isSuperuser && (!hasAnyRole || !canAssetsCreate))
                        ? "No tiene permisos para agregar activos"
                        : ""
                  }
                >
                  <span style={{ display: 'inline-block' }}>
                    <Button 
                      className="add-button" 
                      onClick={() => showModal()} 
                      loading={loading}
                      disabled={
                        (!isSuperuser && (!hasAnyRole || !canAssetsCreate)) || 
                        (maxAssetsAllowed && assets.length >= maxAssetsAllowed)
                      }
                    >
                      <PlusOutlined />
                      Agregar Activo
                    </Button>
                  </span>
                </Tooltip>
              </Col>
              <Col span={6} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <AssetSearch
                  assetsData={assets}
                  searchText={searchText}
                  setSearchText={setSearchText}
                  setFilteredData={setFilteredData}
                />
              </Col>
            </Row>

            <Row justify="end" gutter={[8, 0]}>
              {
                isSuperuser &&
                <Col>
                  <Button 
                    type="primary"
                    className="view-deleted-button"
                    onClick={() => {
                      const params = new URLSearchParams();
                      if (assetsFromUrl.length) params.set('assets', assetsFromUrl.join(','));
                      if (areasFromUrl.length) params.set('areas', areasFromUrl.join(','));
                      navigate(`/assets-deleted${params.toString() ? `?${params.toString()}` : ''}`);
                    }}>
                    <AppstoreFilled />
                    Ver Borrados
                  </Button>
                </Col>
              }
              <Col>
                <ExportButton
                  api={api}
                  contextHolder={contextHolder}
                  data={filteredData}
                  filename="Activos.csv"
                  transformData={(data) =>
                    data.map(item => ({
                      Nombre: item.name,
                      Descripción: item.description,
                      Responsable: item.area?.name || 'N/A',
                      Categoría: item.category?.name || 'N/A',
                      "Creado por": item.user?.username || 'N/A',
                    }))
                  }
                />
              </Col>
            </Row>
          </div>

          <Spin className="custom-spin" spinning={loading}>
            <Table
              className="custom-pagination"
              columns={Columns(hasPermission, showModal, handleDelete, assetTableFilter(), isSuperuser)} 
              dataSource={filteredData} 
              scroll={{ y: 450 }}
              rowKey="code"
              pagination={{
                defaultPageSize: 30,
                showSizeChanger: true,
                pageSizeOptions: ['30', '50', '100'],
                showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} registros`
              }}
              onChange={handleTableChange}
            />
          </Spin>

          {/* Modal para Agregar/Editar */}
          <Modal
            className="dependency-modal"
            title={
              <span>
                <ApiFilled style={{ marginRight: 8 }} />
                {editingAsset ? "Editar Activo" : "Agregar Activo"}
              </span>
            } 
            open={isModalOpen}
            onCancel={() => setIsModalOpen(false)}
            onOk={() => form.submit()}
            cancelButtonProps={{ className: 'cancel-button' }}
            okButtonProps={{ className: 'save-type-button' }}
            cancelText="Cancelar"
            okText={
              <span>
                <SaveFilled style={{ marginRight: 6 }} />
                Guardar
              </span>
            }
            footer={[
              <Button key="cancel" className="cancel-button" onClick={() => setIsModalOpen(false)}>
                Cancelar
              </Button>,
              <Button key="save" className="save-type-button" onClick={() => form.submit()} icon={<SaveFilled />}>
                Guardar
              </Button>
            ]}
          >
            <Form form={form} layout="vertical" onFinish={handleSave}>
              <Form.Item name="name" label="Nombre" rules={[{ required: true, message: "Campo requerido" }]}> 
                <Input.TextArea
                  ref={inputRef}
                  maxLength={50} 
                  showCount
                  style={{ resize: 'none', width: '100%' }} 
                />
              </Form.Item>
              <Form.Item name="description" label="Descripción" rules={[{ required: true, message: "Campo requerido" }]}>
                <Input.TextArea 
                  maxLength={300} 
                  showCount 
                  style={{ resize: 'none', width: '100%', height: '120px' }} 
                />
              </Form.Item>
              <Form.Item name="area_id" label="Responsable" rules={[{ required: true, message: "Responsable requerido" }]}>
                <Select
                  showSearch
                  placeholder="Seleccione un Área"
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option?.children?.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {controlsArea.map((area) => (
                    <Option key={area.id} value={area.id}>
                      {area.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item name="category_id" label="Categoría" rules={[{ required: true, message: "Categoría requerida" }]}>
                <Select
                  showSearch
                  placeholder="Seleccione una Categoría"
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option?.children?.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {categories.map((category) => (
                    <Option key={category.id} value={category.id}>
                      {category.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Form>
          </Modal>
        </div>
        <Row style={{ marginTop: 80 }}>
          <Assetia
            pageTitle="activos"
            pageDescription="cargar tus activos y gestionarlos de manera simple"
            view="assets"
          />
        </Row>
      </>
    </WithPermission>
  );
};

export default Assets;
